import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Award, BookOpen, Info, Star } from "react-feather";
import BigNumber from "bignumber.js";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";
import { PublicKey } from "@solana/web3.js";
import { gsap } from "gsap";
import { useConnection } from "@solana/wallet-adapter-react";
import { Tooltip } from "react-tooltip";
import { NumericFormat } from "react-number-format";
import { abbreviateUSD } from "@axvdex/utils/formatNumber";
import imgSanitize from "@axvdex/utils/imgSanitize";
import { useAppSelector } from "@axvdex/state";
import { selectWalletInfo } from "@axvdex/state/wallet/walletSelectors";
import useLanguage from "@axvdex/hooks/useLanguage";
import SolanaSaleBidSection from "@axvdex/components/outbid/solana/SolanaSaleBidSection";
import { responsiveBreakpoints } from "@axvdex/constants";
import SolanaSaleDocsModal from "@axvdex/components/outbid/solana/SolanaSaleDocsModal";
import { useGetSales, useGetBidderInfo } from "@axvdex/hooks/solana/useOutbid";
import {
  Bidder,
  DigitalAsset,
  OutbidSale,
  Sale,
  SaleStatus,
  SpecificBidAsset,
  TopBidder,
} from "@axvdex/types/solana/outbid";
import { ListItemsProps } from "@axvdex/components/form-element/CustomSelect";
import CustomNumericInput from "@axvdex/components/form-element/CustomNumericInput";
import Button from "@axvdex/components/common/Button";
import { TimeLeftComponent } from "@axvdex/components/outbid/common/TimeLeftComponent";
import { TimeLeftTooltip } from "@axvdex/components/outbid/common/TimeLeftTooltip";
import ActivityMessageHistoryComponent from "@axvdex/components/outbid/common/ActivityMessageHistoryComponent";
import { ProgressBarComponent } from "@axvdex/components/outbid/common/ProgressBarComponent";
import { useSubscribeToBidderInfo, useSubscribeToSale } from "@axvdex/hooks/solana/useSubscribeOutbid";
import UserSettingsModal from "@axvdex/components/modals/UserSettingsModal";
import CustomLoader from "@axvdex/components/common/CustomLoader";
import { useNicknameMap } from "@axvdex/hooks/solana/useNickname";
import { noNicknamesCheck } from "@axvdex/utils/nicknamesSanitization";
import { useUserSettings } from "@axvdex/hooks/useUserSettings";
import { ReactComponent as IcnTrophy } from "../../../assets/icons/icn-trophy.svg";
import SolanaSalesBar from "./SolanaSalesBar";
import SolanaSaleMyTicketsModal from "./SolanaSaleMyTicketsModal";
import { TopBiddersComponent } from "./SolanaSaleTopBidder";
import SaleRedeemSection, { NFTModal } from "./SolanaSaleRedeemSection";
import RefundButton from "./SolanaSaleRefundButton";

function saleToListItem(sale: Sale): ListItemsProps {
  return {
    id: sale.saleAccount.toBase58(),
    value: sale.id.toString(),
    label: `Sale ${sale.id}`,
    extraFields: {
      isActive: sale.status === "Bidding",
      withBid: false,
      isEnded: sale.status.startsWith("Ended"),
      isWaiting: sale.status === "WaitingToStart",
    },
  };
}

type SolanaSaleMainProps = {
  selectedOutbidSale: OutbidSale;
  setSelectedOutbidSale: (sale: OutbidSale) => void;
};

const SolanaSaleMain = ({ selectedOutbidSale, setSelectedOutbidSale }: SolanaSaleMainProps) => {
  const [sales, setSales] = useState<Sale[]>([]);
  const { selectedSale, updateSaleAccount } = useSubscribeToSale(null);
  const { bidderInfo, updateSaleAccount: updateSaleAccountForBidderInfo } = useSubscribeToBidderInfo(null);
  const [onWhitelist, setOnWhitelist] = useState<boolean>(false);
  const [saleDocsModalOpen, setSaleDocsModalOpen] = useState(false);
  const { connection } = useConnection();
  const [selectedSaleItem, setSelectedSaleItem] = useState<ListItemsProps | null>(null);
  const [saleItemList, setSaleItemList] = useState<ListItemsProps[]>([]);

  useEffect(() => {
    if (selectedSale) {
      setSelectedSaleItem(saleToListItem(selectedSale));
      const saleIdx = sales.findIndex(sale => sale.saleAccount.toBase58() === selectedSale.saleAccount.toBase58());
      if (saleIdx !== -1) {
        sales[saleIdx] = selectedSale;
      }
    }
    setSaleItemList(sales.map(saleToListItem));
  }, [selectedSale]);

  useEffect(() => {
    const fetchSaleData = async () => {
      const sales: Sale[] = await useGetSales(connection, selectedOutbidSale.sales);
      setSales(sales);
      if (sales.length > 0) {
        // show the last sale by default
        const initialSaleAccount = sales[sales.length - 1].saleAccount;
        updateSaleAccount(initialSaleAccount);
        updateSaleAccountForBidderInfo(initialSaleAccount);
      }
    };
    fetchSaleData();
  }, [selectedOutbidSale]);

  if (selectedSale === null) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
        }}
      >
        <CustomLoader size="sm" />
      </div>
    );
  }

  const setSelectedSale = (slectedSaleProp: ListItemsProps) => {
    const selectedSaleAccount = sales.find(sale => sale.saleAccount.toBase58() === slectedSaleProp.id).saleAccount;
    updateSaleAccount(selectedSaleAccount);
    updateSaleAccountForBidderInfo(selectedSaleAccount);
  };

  return (
    <>
      <HeaderComponent
        selectedOutbidSale={selectedOutbidSale}
        selectedSale={selectedSale}
        bidderInfo={bidderInfo}
        setSelectedOutbidSale={setSelectedOutbidSale}
        setSaleDocsModalOpen={setSaleDocsModalOpen}
      />

      {sales.length > 0 && (
        <SolanaSalesBar
          salesList={saleItemList}
          selectedSale={selectedSaleItem}
          setSelectedSale={setSelectedSale}
          outbidSaleSelected={selectedOutbidSale.name}
        />
      )}
      <br />
      <InfoComponent
        selectedOutbidSale={selectedOutbidSale}
        selectedSale={selectedSale}
        bidderInfo={bidderInfo}
        saleDocsModalOpen={saleDocsModalOpen}
        setSaleDocsModalOpen={setSaleDocsModalOpen}
        onWhitelist={onWhitelist}
        setOnWhitelist={setOnWhitelist}
      />
    </>
  );
};

const StatusComponent = ({ status }: { status: string }) => {
  const { i18 } = useLanguage();

  const parseStatusText = (statusText: string) => {
    switch (statusText) {
      case "WaitingToStart":
        return i18("Waiting to start", "sale.details.waitingToStart");
      case "WhitelistBidding":
        return i18("VIP Bidding", "sale.details.vipBidding");
      case "Bidding":
        return i18("Bidding", "sale.details.bidding");
      case "BonusBidding":
        return i18("BONUS", "sale.details.bonus");
      case "EndedSoldOut":
        return i18("Sold Out", "sale.details.soldOut");
      case "EndedNotSoldOut":
        return i18("Not Sold Out", "sale.details.notSoldOut");
      case "EndedBonusTimeSoldOut":
        return i18("Bonus Sold Out", "sale.details.bonusSoldOut");
      default:
        return "";
    }
  };

  return (
    <div style={{ display: "flex", marginLeft: "auto" }}>
      <h3>{i18("Status", "sale.details.status")}:</h3>
      <h3 style={{ marginLeft: ".5em" }}>{parseStatusText(status)}</h3>
    </div>
  );
};

const TotalRaisedComponent = ({ calcTotalRaised }: { calcTotalRaised: () => number }) => {
  const { i18 } = useLanguage();
  const app = useRef<HTMLDivElement>(null);
  const [calcTotalRaisedValue, setCalcTotalRaisedValue] = useState<number | null>(null);

  useEffect(() => {
    setCalcTotalRaisedValue(calcTotalRaised());
  }, [calcTotalRaised]);

  return (
    <div ref={app}>
      <p className="textGrey" style={{ marginBottom: ".5rem" }}>
        {i18("Total raised", "sale.details.totalRaised")}
      </p>

      <CustomNumericInput
        extraClassName="gradientText totalRaised"
        name="totalRaised"
        labelText={i18("Total raised", "sale.details.totalRaised")}
        hiddenLabel={true}
        placeholder={"0"}
        prefix="$"
        value={calcTotalRaisedValue ?? ""}
        disabled={true}
        style={{ maxWidth: "8em" }}
      />
    </div>
  );
};

const UserTotalTicketsComponent = ({
  selectedOutbidSale,
  selectedSale,
  bidderInfo,
}: {
  bidderInfo: Bidder | null;
  selectedSale: Sale;
  selectedOutbidSale: OutbidSale;
}) => {
  const { i18 } = useLanguage();
  const walletInfo = useAppSelector(selectWalletInfo);
  const [totalUserTickets, setTotalUserTickets] = useState(null);
  const app = useRef<HTMLDivElement>(null);
  const prevAmount = useRef({ totalUserTickets }).current;
  const { connection } = useConnection();

  useEffect(() => {
    if (totalUserTickets && totalUserTickets !== prevAmount.totalUserTickets) {
      const ctx = gsap.context(() => {
        gsap.from(`.userTotalTickets`, 1, {
          ease: "Power3.easeInOut",
          duration: 0.5,
          y: "50%",
          opacity: 0,
        });
      }, app);
      return () => ctx.revert();
    }
  }, [totalUserTickets]);

  useEffect(() => {
    const fetchTotalUserTickets = async () => {
      if (selectedOutbidSale && walletInfo.isConnected) {
        // Fetch the bidder info accounts for each sale of this outbid
        const bidderAccounts = await Promise.all(
          selectedOutbidSale.sales.map(saleAccount =>
            useGetBidderInfo(connection, saleAccount, new PublicKey(walletInfo.walletAddress))
          )
        );
        // Reduce the total tickets of the user
        let totalUserTickets = BigNumber(0);
        bidderAccounts.forEach(bidderAccount => {
          if (bidderAccount !== null) {
            totalUserTickets = totalUserTickets.plus(bidderAccount.boughtTicketsAmount);
          }
        });
        setTotalUserTickets(totalUserTickets);
      }
    };
    fetchTotalUserTickets();
  }, [selectedOutbidSale, bidderInfo]);

  if (!walletInfo.isConnected) {
    return null;
  }

  return (
    <div ref={app}>
      <p className="textGrey" style={{ marginBottom: ".5rem" }}>
        {i18("Your total tickets", "sale.details.userTotalTickets")}
      </p>
      {totalUserTickets !== null && (
        <CustomNumericInput
          extraClassName="gradientText userTotalTickets"
          name="userTotalTickets"
          labelText={i18("Your total tickets", "sale.details.userTotalTickets")}
          hiddenLabel={true}
          placeholder={"0"}
          value={BigNumber(totalUserTickets)
            .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
            .decimalPlaces(2)
            .toNumber()}
          disabled={true}
          style={{ maxWidth: "8em" }}
        />
      )}
    </div>
  );
};

type SolanaSaleDetailsProps = {
  selectedSale: Sale;
  selectedOutbidSale: OutbidSale;
  setSaleDocsModalOpen: (open: boolean) => void;
  bidderInfo: Bidder | null;
  onWhitelist: boolean;
};

const SolanaSaleDetails = ({
  selectedSale,
  selectedOutbidSale,
  setSaleDocsModalOpen,
  bidderInfo,
  onWhitelist,
}: SolanaSaleDetailsProps) => {
  const { i18 } = useLanguage();
  const walletInfo = useAppSelector(selectWalletInfo);
  const [nftModalOpen, setNFTModalOpen] = useState(false);
  const [paginationMyBidsModal, setPaginationMyBidsModal] = useState({
    pageIndex: 0,
    pageSize: 5,
  });
  const { nicknames } = useNicknameMap(true);
  const { userSettings } = useUserSettings();

  useEffect(() => {
    displayBonusComponent();
  }, [userSettings]);

  const isElegibleForNFT = () => {
    return !!(
      ["EndedSoldOut", "EndedNotSoldOut", "EndedBonusTimeSoldOut"].includes(selectedSale.status) &&
      selectedSale.topBidders &&
      selectedSale.topBidders.find((topBidder: TopBidder) => topBidder.address.toBase58() === walletInfo.walletAddress)
    );
  };

  const displayExtraMessages = () => {
    const msgs = [];
    if (bidderInfo !== null && bidderInfo.bids.length > 0) {
      if (false === onWhitelist) {
        msgs.push(i18("Bid now to secure your VIP spot for the next round(s)", "sales.bidNow"));
      }
    }
    if ("BonusBidding" === selectedSale.status) {
      if (selectedSale.lastestBidder.toBase58() === walletInfo.walletAddress) {
        msgs.push(i18("You are holding the bag", "sales.holdingBag"));
      } else {
        msgs.push(i18("Bid now to hold the bag, if time is up and not sold out, you win", "sales.bidToHoldBag"));
      }
      msgs.push(i18("Each new bid restarts the bonus time", "sales.eachNewBidRestartsBonusTime"));
    }
    return msgs;
  };

  const displayBonusComponent = () => {
    if (!selectedOutbidSale || !selectedSale || "BonusBidding" !== selectedSale.status) return null;

    const remainingTickets = selectedSale.config.targetAllocation.targetTicketAmount
      .minus(selectedSale.saleState.ticketsCount)
      .div(Math.pow(10, BigNumber(selectedSale.config.targetAllocation.targetAssetDecimals).toNumber()))
      .decimalPlaces(0)
      .toString(10);

    return (
      <div style={{ backgroundColor: "#d9480b", width: "100%", borderRadius: "1em", padding: "1em" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: ".5em" }}>
          <Star style={{ width: "1.5em", marginRight: "1em" }} />
          <span style={{ fontSize: "1.25em" }}>{"Bonus Time"}</span>
          <Info data-tooltip-id="tooltip_bonusTime" style={{ marginLeft: "auto", width: "1em" }} />
          <Tooltip
            id="tooltip_bonusTime"
            place="bottom"
            style={{ backgroundColor: "var(--dark)", color: "white", zIndex: 3 }}
            render={() => (
              <div>
                <span>{i18("Each new bid restarts the bonus time", "sales.eachNewBidRestartsBonusTime")}</span>
              </div>
            )}
          />
        </div>
        <div style={{ textAlign: "left", marginLeft: "2.5em" }}>
          <span style={{ fontSize: "1em", color: "var(--warm-grey)" }}>
            <NumericFormat
              className="ticketAmount"
              displayType="text"
              thousandSeparator=","
              decimalSeparator="."
              prefix={
                (nicknames[selectedSale.lastestBidder.toBase58()]
                  ? noNicknamesCheck(nicknames[selectedSale.lastestBidder.toBase58()].nickname, userSettings)
                  : "------") + i18(" is holding the ", "sales.isHoldingThe")
              }
              suffix={"💰"}
              value={remainingTickets}
            />
          </span>
        </div>
      </div>
    );
  };

  const displaySoldOutIncentivesComponent = () => {
    if (!selectedOutbidSale || !selectedSale) return null;

    const incentives = selectedSale.soldOutBidderIncentives;
    let incentiveTitle = null;
    let incentiveDetails = null;
    let nextIncentiveNFT = null;
    if (
      incentives &&
      incentives.length > 0 &&
      !["EndedSoldOut", "EndedNotSoldOut", "EndedBonusTimeSoldOut", "Canceled"].includes(selectedSale.status)
    ) {
      incentiveTitle =
        "Reward: " +
        incentives
          .map((incentive: DigitalAsset) => {
            if (incentive.isAsset) {
              const asset = selectedOutbidSale.assets[incentive.mint.toBase58()];
              return `${BigNumber(incentive.amount).div(Math.pow(10, asset?.decimals)).toString(10)} ${asset.symbol}`;
            }
            if (incentive.isNFT) {
              nextIncentiveNFT = incentive;
              return `NFT`;
            }
          })
          .join(", ");

      incentiveDetails = `to the bidder that closes out this sale`;
    }

    const NftRender = ({ nft }: { nft: SpecificBidAsset }) => {
      const [nftImage, setNftImage] = useState<string | null>(null);
      useEffect(() => {
        getNftDetails();
      }, []);
      const getNftDetails = async () => {
        // TODO: NFT
        // 1. Get NFT details
        // 2. Set NFT image
      };
      return (
        <div>
          <img src={nftImage} alt={"NFT"} style={{ width: "25em" }} />
        </div>
      );
    };

    if (!incentiveTitle) return null;
    return (
      <div
        style={{ backgroundColor: "#8B8000", width: "100%", borderRadius: "1em", padding: "1em", marginTop: ".5em" }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <IcnTrophy style={{ width: "1.5em", marginRight: "1em" }} />
          <div>
            <span style={{ fontSize: "1.25em" }}>{incentiveTitle}</span>
            <br />
            <small>{incentiveDetails}</small>
          </div>
          {nextIncentiveNFT && (
            <>
              <Info data-tooltip-id="tooltip_individualIncentive" style={{ marginLeft: "auto", width: "1em" }} />
              <Tooltip
                className={"opaque"}
                id="tooltip_individualIncentive"
                place="bottom"
                opacity={1}
                style={{ backgroundColor: "var(--dark)", color: "white", zIndex: 3 }}
                render={() => <NftRender nft={nextIncentiveNFT} />}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="withGradientBorder">
        <div style={{ display: "flex", marginBottom: "1em" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <TimeLeftComponent
              status={selectedSale.status}
              startTimestamp={selectedSale.saleState.startTimestamp}
              whitelistDuration={selectedSale.config.saleSettings.whitelistDuration}
              duration={selectedSale.config.saleSettings.duration}
              bonusDuration={selectedSale.config.saleSettings.bonusDuration}
              endTimestamp={selectedSale.saleState.endTimestamp}
            />
            <TimeLeftTooltip
              status={selectedSale.status}
              startTimestamp={selectedSale.saleState.startTimestamp}
              whitelistDuration={selectedSale.config.saleSettings.whitelistDuration}
              bonusDuration={selectedSale.config.saleSettings.bonusDuration}
              endTimestamp={selectedSale.saleState.endTimestamp}
            />
          </div>
          <div className="inlineFlexbox" style={{ marginLeft: "auto" }}>
            <div style={{ alignSelf: "center" }}>
              <div className="inlineFlexbox">
                <p className="textGrey" style={{ marginBottom: "0px", textAlign: "right" }}>
                  {i18("Current raised", "sales.currentRaised")}
                </p>
              </div>
              <div style={{ fontSize: "1.5em" }}>
                {abbreviateUSD(
                  BigNumber(selectedSale.saleState.ticketsCount)
                    .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
                    .decimalPlaces(6)
                    .toString(10)
                ) +
                  " of " +
                  abbreviateUSD(
                    BigNumber(selectedSale.config.targetAllocation.targetTicketAmount)
                      .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
                      .decimalPlaces(6)
                      .toString(10)
                  )}
              </div>
            </div>
          </div>
        </div>

        <ProgressBarComponent
          isComplete={["EndedSoldOut", "EndedNotSoldOut", "EndedBonusTimeSoldOut"].includes(selectedSale.status)}
          tickets_count={selectedSale.saleState.ticketsCount.toString()}
          target_ticket_amount={selectedSale.config.targetAllocation.targetTicketAmount.toString()}
          status={selectedSale.status}
        />

        <div
          style={{
            display: "flex",
            marginTop: "1em",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ marginRight: ".5em" }}>
              {isElegibleForNFT()
                ? i18("Your NFT/tickets on", "sale.yourTicketsAndNFTs")
                : i18("Your tickets on", "sale.yourTickets")}
            </span>
            {bidderInfo !== null && bidderInfo.bids.length > 0 ? (
              <SolanaSaleMyTicketsModal
                outbidSale={selectedOutbidSale}
                selectedSale={selectedSale}
                bidderInfo={bidderInfo}
                paginationMyBidsModal={paginationMyBidsModal}
                setPaginationMyBidsModal={setPaginationMyBidsModal}
              />
            ) : (
              <> this sale </>
            )}
          </div>

          <span
            style={{
              backgroundImage: "linear-gradient(92.22deg, #0BF6CC 0.58%, #073DC8 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              marginLeft: "auto",
            }}
          >
            {bidderInfo === null
              ? 0
              : BigNumber(bidderInfo.boughtTicketsAmount.toString(10))
                .div(Math.pow(10, selectedSale.config.targetAllocation.targetAssetDecimals.toNumber()))
                .toNumber()}
          </span>

          {
            // Show nft modal button if sale has ended and user is on the top bidders list
            isElegibleForNFT() && (
              <>
                <Button
                  style={{ marginLeft: "1em" }}
                  title={i18("See your NFTs", "sale.nfts.title")}
                  btnVariant="icon"
                  btnColor="dark-medium"
                  icon={<Award />}
                  onClick={() => {
                    setNFTModalOpen(true);
                  }}
                  btnSize="sm"
                />
                <NFTModal
                  NftModalOpen={nftModalOpen}
                  setNFTModalOpen={setNFTModalOpen}
                  saleID={selectedSale.id}
                  selectedSale={selectedSale}
                  topNumber={selectedSale.topBidders.findIndex(
                    topBidder => topBidder.address.toBase58() === walletInfo.walletAddress
                  )}
                  bidderInfo={bidderInfo}
                  outbidSale={selectedOutbidSale}
                />
              </>
            )
          }
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <RefundButton selectedSale={selectedSale} />
        </div>

        {displayBonusComponent()}
        {displaySoldOutIncentivesComponent()}
        <ActivityMessageHistoryComponent sale={selectedSale} />

        <div
          style={{
            borderTop: "var(--border)",
            marginTop: "1em",
            paddingTop: "1em",
            fontSize: ".85em",
          }}
        >
          {0 < displayExtraMessages().length && (
            <ul style={{ paddingLeft: "var(--px24)" }}>
              {displayExtraMessages().map((msg, i) => (
                <li key={"displayMsg_" + i}>{msg}</li>
              ))}
            </ul>
          )}
          <Button
            style={{ padding: ".5em", fontSize: ".80em" }}
            btnColor="gradientText"
            text={i18("More about this sale", "sale.details.moreAboutThisSale")}
            title={i18("More about this sale", "sale.details.moreAboutThisSale")}
            onClick={() => setSaleDocsModalOpen(true)}
          />
        </div>
      </div>
    </div>
  );
};

type HeaderComponentProps = {
  selectedOutbidSale: OutbidSale;
  setSelectedOutbidSale: (selectedOutbidSale: OutbidSale) => void;
  bidderInfo: Bidder | null;
  selectedSale: Sale | null;
  setSaleDocsModalOpen: (open: boolean) => void;
};
const HeaderComponent = ({
  selectedOutbidSale,
  setSelectedOutbidSale,
  bidderInfo,
  selectedSale,
  setSaleDocsModalOpen,
}: HeaderComponentProps) => {
  const { i18 } = useLanguage();
  const navigate = useNavigate();
  const walletInfo = useAppSelector(selectWalletInfo);

  const calcTotalRaised = () => {
    return BigNumber(selectedOutbidSale.raised).div(Math.pow(10, 6)).decimalPlaces(2).toNumber();
  };

  return (
    <div className="salesDetailsHeader flexbox" style={{ marginBottom: "2em" }}>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div className="inlineFlexbox">
          <Button
            style={{ marginRight: ".5em" }}
            title={i18("Go back", "sales.goBack")}
            icon={<ArrowLeft />}
            onClick={() => {
              setSelectedOutbidSale(null);
              navigate("/outbid");
            }}
            btnSize="sm"
          />
          {selectedOutbidSale.projectSymbol && (
            <img src={imgSanitize(selectedOutbidSale.projectSymbol)} alt={selectedOutbidSale.name} />
          )}
          <h1>{selectedOutbidSale.name}</h1>
        </div>
        <div className="inlineFlexbox" style={{ marginTop: "1em" }}>
          <Button
            style={{ marginRight: "1.5em" }}
            title={i18("Documentation", "sales.docs")}
            icon={<BookOpen />}
            onClick={() => setSaleDocsModalOpen(true)}
            btnSize="sm"
          >
            {i18("Documentation", "sales.docs")}
          </Button>
          {walletInfo.isConnected && <UserSettingsModal style={"button"} buttonTitle="Nickname" />}
        </div>
      </div>
      {selectedSale && (
        <section className="withGradientBorder" style={{ marginLeft: "auto" }}>
          <div style={{ display: "flex" }}>
            <UserTotalTicketsComponent
              selectedOutbidSale={selectedOutbidSale}
              selectedSale={selectedSale}
              bidderInfo={bidderInfo}
            />
            <TotalRaisedComponent calcTotalRaised={calcTotalRaised} />
          </div>
        </section>
      )}
    </div>
  );
};

type InfoComponentProps = {
  selectedSale: Sale | null;
  selectedOutbidSale: OutbidSale;
  bidderInfo: Bidder | null;
  saleDocsModalOpen: boolean;
  setSaleDocsModalOpen: (open: boolean) => void;
  onWhitelist: boolean;
  setOnWhitelist: (open: boolean) => void;
};

const ACTIVE_STATE = new Set<SaleStatus>(["Bidding", "BonusBidding", "WhitelistBidding"]);
const ENDED_STATE = new Set<SaleStatus>(["EndedSoldOut", "EndedNotSoldOut", "EndedBonusTimeSoldOut"]);

const InfoComponent = ({
  selectedSale,
  selectedOutbidSale,
  bidderInfo,
  saleDocsModalOpen,
  setSaleDocsModalOpen,
  onWhitelist,
  setOnWhitelist,
}: InfoComponentProps) => {
  const topBidders: TopBidder[] = selectedSale?.topBidders ?? [];
  if (!selectedSale) {
    return null;
  }
  return (
    <>
      {!useMediaQuery(responsiveBreakpoints.mobileSm) ? (
        <div style={{ display: "flex" }}>
          <div style={{ width: "50%" }}>
            {ACTIVE_STATE.has(selectedSale.status) && (
              <SolanaSaleBidSection
                outbidSale={selectedOutbidSale}
                selectedSale={selectedSale}
                bidderInfo={bidderInfo}
                onWhitelist={onWhitelist}
                setOnWhitelist={setOnWhitelist}
              />
            )}
            {ENDED_STATE.has(selectedSale.status) && (
              <SaleRedeemSection outbidSale={selectedOutbidSale} selectedSale={selectedSale} bidderInfo={bidderInfo} />
            )}
            {selectedSale && (
              <TopBiddersComponent
                topBidders={topBidders}
                selectedSale={selectedSale}
                selectedOutbidSale={selectedOutbidSale}
              />
            )}
          </div>
          <div style={{ width: "50%" }}>
            {selectedSale && <StatusComponent status={selectedSale.status} />}
            <SolanaSaleDetails
              selectedSale={selectedSale}
              selectedOutbidSale={selectedOutbidSale}
              bidderInfo={bidderInfo}
              setSaleDocsModalOpen={setSaleDocsModalOpen}
              onWhitelist={onWhitelist}
            />
          </div>
        </div>
      ) : (
        <div>
          {selectedSale && <StatusComponent status={selectedSale.status} />}
          <SolanaSaleDetails
            selectedSale={selectedSale}
            selectedOutbidSale={selectedOutbidSale}
            bidderInfo={bidderInfo}
            setSaleDocsModalOpen={setSaleDocsModalOpen}
            onWhitelist={onWhitelist}
          />
          <br />
          {ACTIVE_STATE.has(selectedSale.status) && (
            <SolanaSaleBidSection
              outbidSale={selectedOutbidSale}
              selectedSale={selectedSale}
              bidderInfo={bidderInfo}
              onWhitelist={onWhitelist}
              setOnWhitelist={setOnWhitelist}
            />
          )}
          <br />
          {ENDED_STATE.has(selectedSale.status) && (
            <SaleRedeemSection outbidSale={selectedOutbidSale} selectedSale={selectedSale} bidderInfo={bidderInfo} />
          )}
          <br />
          {selectedSale && (
            <TopBiddersComponent
              topBidders={topBidders}
              selectedSale={selectedSale}
              selectedOutbidSale={selectedOutbidSale}
            />
          )}
        </div>
      )}
      <SolanaSaleDocsModal
        saleDocsModalOpen={saleDocsModalOpen}
        setSaleDocsModalOpen={setSaleDocsModalOpen}
        selectedOutbidSale={selectedOutbidSale}
      />
    </>
  );
};

export default SolanaSaleMain;
